import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
    ArrowLeft, Store, ShieldCheck, Star, StarOff, Package, ShoppingBag, Users,
    Image as ImageIcon, Search, Filter, CheckSquare, Square, ChevronLeft, ChevronRight,
    SortAsc, SortDesc
} from "lucide-react";
import { adminApi } from "@/services/api/admin";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { formatPrice, toNum } from "@/utils/formatters";
// Si usas una base para las imágenes (uploads), mantenla. Si no, deja fileUrl tal cual.
import { UPLOADS_URL } from "@/utils/utils";

type SellerUser = {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    user_type: "seller" | "customer" | "admin";
    email_verified_at?: string | null;
    seller_id?: number;
};

type StoreImage = {
    image_url: string;
    alt_text?: string | null;
    is_primary?: number | boolean;
    sort_order?: number;
};

type StoreProduct = {
    id: number;
    seller_id: number;
    name: string;
    slug: string;
    sku: string;
    price: string | number;
    compare_price?: string | number | null;
    status: "active" | "inactive" | string;
    stock_quantity: number;
    rating_average?: string | number;
    rating_count?: number;
    images?: StoreImage[];
    is_featured?: number | boolean;
    created_at?: string;
    updated_at?: string;
};

type AdminSellerStore = {
    id: number;
    user_id: number;
    store_name: string;
    store_description?: string | null;
    store_logo?: string | null;
    store_banner?: string | null;
    verification_status: "pending" | "verified" | "rejected";
    rating_average: string | number;
    total_reviews: number | string;
    total_sales: number | string;
    is_featured: number | boolean;
    commission_rate: string | number;
    created_at: string;
    updated_at: string;
    user: SellerUser;
    products: StoreProduct[];
};

const isTrue = (v: any) => v === true || v === 1;
const fileUrl = (p?: string | null) => (p ? p : "");

// Opcional: límite máximo de destacados que deseas permitir en Home (0 = sin límite)
const MAX_FEATURED = 12;

type SortKey = "created_desc" | "created_asc" | "price_desc" | "price_asc" | "name_asc" | "name_desc" | "rating_desc";
const SORT_OPTIONS: { value: SortKey; label: string; icon?: React.ReactNode }[] = [
    { value: "created_desc", label: "Más recientes", icon: <SortDesc className="w-4 h-4" /> },
    { value: "created_asc", label: "Más antiguos", icon: <SortAsc className="w-4 h-4" /> },
    { value: "price_desc", label: "Precio: alto a bajo" },
    { value: "price_asc", label: "Precio: bajo a alto" },
    { value: "name_asc", label: "Nombre A-Z" },
    { value: "name_desc", label: "Nombre Z-A" },
    { value: "rating_desc", label: "Mejor puntuación" },
];

const AdminSellerStorePage: React.FC = () => {
    const { sellerId } = useParams<{ sellerId: string }>();
    const [data, setData] = useState<AdminSellerStore | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [busyFeatureIds, setBusyFeatureIds] = useState<Set<number>>(new Set());

    // --- Controles locales ---
    const [q, setQ] = useState("");
    const [filterFeatured, setFilterFeatured] = useState(false);
    const [filterInStock, setFilterInStock] = useState(false);
    const [filterStatus, setFilterStatus] = useState<"all" | "active" | "inactive">("all");
    const [prioritizeFeatured, setPrioritizeFeatured] = useState(true);
    const [sortBy, setSortBy] = useState<SortKey>("created_desc");

    // Selección y acciones masivas
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const hasSelection = selected.size > 0;
    const clearSelection = () => setSelected(new Set());

    // Paginación local
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setErr(null);
                const store = await adminApi.getSellerStore(Number(sellerId));
                if (!mounted) return;
                setData(store);
            } catch (e: any) {
                const msg = e?.response?.data?.message || e?.message || "No se pudo cargar la tienda";
                setErr(msg);
                toast.error(msg);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [sellerId]);

    // Métricas básicas del header
    const metrics = useMemo(() => {
        const rating = toNum(data?.rating_average);
        const sales = toNum(data?.total_sales);
        const commission = toNum(data?.commission_rate);
        const productsCount = data?.products?.length || 0;
        return { rating, sales, commission, productsCount };
    }, [data]);

    const currentFeaturedCount = useMemo(
        () => (data?.products || []).filter((p) => isTrue(p.is_featured)).length,
        [data]
    );

    // --- Derivados locales (filtro + orden + priorización) ---
    const filteredSorted = useMemo(() => {
        let arr = [...(data?.products || [])];

        // Buscar (nombre o SKU)
        const term = q.trim().toLowerCase();
        if (term) {
            arr = arr.filter((p) => {
                const nm = p.name?.toLowerCase() || "";
                const sku = p.sku?.toLowerCase() || "";
                return nm.includes(term) || sku.includes(term);
            });
        }

        // Filtros
        if (filterFeatured) arr = arr.filter((p) => isTrue(p.is_featured));
        if (filterInStock) arr = arr.filter((p) => (p.stock_quantity ?? 0) > 0);
        if (filterStatus !== "all") arr = arr.filter((p) => (p.status || "active") === filterStatus);

        // Orden
        const byDate = (a?: string, b?: string) => {
            const ta = a ? Date.parse(a) : 0;
            const tb = b ? Date.parse(b) : 0;
            return ta - tb;
        };
        //console.log("sin ordenar", arr);
        arr.sort((a, b) => {
            switch (sortBy) {
                case "created_asc": return byDate(a.created_at, b.created_at);
                case "created_desc": return byDate(b.created_at, a.created_at);
                case "price_asc": return toNum(a.price) - toNum(b.price);
                case "price_desc": return toNum(b.price) - toNum(a.price);
                case "name_asc": return String(a.name).localeCompare(String(b.name));
                case "name_desc": return String(b.name).localeCompare(String(a.name));
                case "rating_desc": return toNum(b.rating_average) - toNum(a.rating_average);
                default: return 0;
            }
        });
        //console.log("ordenado", arr);
        // Priorizar destacados arriba (sin perder el orden elegido)
        if (prioritizeFeatured) {
            const featured = arr.filter((p) => isTrue(p.is_featured));
            const regular = arr.filter((p) => !isTrue(p.is_featured));
            arr = [...featured, ...regular];
        }

        return arr;
    }, [data, q, filterFeatured, filterInStock, filterStatus, sortBy, prioritizeFeatured]);

    // --- Paginación local ---
    const total = filteredSorted.length;
    const lastPage = Math.max(1, Math.ceil(total / perPage));
    useEffect(() => { if (page > lastPage) setPage(1); }, [lastPage, page]);
    const pageItems = useMemo(() => {
        const start = (page - 1) * perPage;
        return filteredSorted.slice(start, start + perPage);
    }, [filteredSorted, page, perPage]);

    // --- Toggle destacado individual ---
    const toggleFeatured = async (productId: number, currentVal?: number | boolean) => {
        if (!data) return;
        const next = !isTrue(currentVal);

        // Enforce límite (si lo deseas)
        if (MAX_FEATURED > 0 && next) {
            const willBe = currentFeaturedCount + 1;
            if (willBe > MAX_FEATURED) {
                toast.error(`Límite de destacados alcanzado (${MAX_FEATURED}).`);
                return;
            }
        }

        setBusyFeatureIds((s) => new Set(s).add(productId));

        // Optimista
        setData((prev) => {
            if (!prev) return prev;
            const updated = prev.products.map((p) =>
                p.id === productId ? { ...p, is_featured: next ? 1 : 0 } : p
            );
            return { ...prev, products: updated };
        });

        try {
            await adminApi.setProductFeatured(productId, next);
            toast.success(next ? "Marcado como destacado" : "Quitado de destacados");
        } catch (e: any) {
            // revertir
            setData((prev) => {
                if (!prev) return prev;
                const updated = prev.products.map((p) =>
                    p.id === productId ? { ...p, is_featured: currentVal } : p
                );
                return { ...prev, products: updated };
            });
            const msg = e?.response?.data?.message || e?.message || "No se pudo actualizar el destacado";
            toast.error(msg);
        } finally {
            setBusyFeatureIds((s) => {
                const n = new Set(s);
                n.delete(productId);
                return n;
            });
        }
    };

    // --- Selección múltiple + acciones masivas ---
    const isSelected = (id: number) => selected.has(id);
    const toggleSelect = (id: number) => {
        setSelected((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };
    const toggleSelectAllPage = () => {
        const ids = pageItems.map((p) => p.id);
        const allSelected = ids.every((id) => selected.has(id));
        setSelected((prev) => {
            const n = new Set(prev);
            if (allSelected) {
                ids.forEach((id) => n.delete(id));
            } else {
                ids.forEach((id) => n.add(id));
            }
            return n;
        });
    };

    const bulkSetFeatured = async (makeFeatured: boolean) => {
        if (!data || selected.size === 0) return;

        // Enforce límite total al activar
        if (MAX_FEATURED > 0 && makeFeatured) {
            const currently = (data.products || []).filter((p) => isTrue(p.is_featured)).length;
            // de los seleccionados, cuántos NO están destacados
            const toActivate = (data.products || []).filter((p) => selected.has(p.id) && !isTrue(p.is_featured)).length;
            if (currently + toActivate > MAX_FEATURED) {
                toast.error(`No puedes exceder ${MAX_FEATURED} destacados. Reduce la selección.`);
                return;
            }
        }

        // Optimista
        setData((prev) => {
            if (!prev) return prev;
            const updated = prev.products.map((p) =>
                selected.has(p.id) ? { ...p, is_featured: makeFeatured ? 1 : 0 } : p
            );
            return { ...prev, products: updated };
        });

        // Llamadas (puedes hacer Promise.all, aquí lo hacemos en paralelo)
        const ids = Array.from(selected);
        setBusyFeatureIds((s) => new Set([...Array.from(s), ...ids]));

        try {
            await Promise.all(ids.map((id) => adminApi.setProductFeatured(id, makeFeatured)));
            toast.success(makeFeatured ? "Productos marcados como destacados" : "Productos quitados de destacados");
        } catch (e: any) {
            // Revertir si algo falló: para simplicidad, reconsultar la tienda o revertir selección
            // Aquí revertimos localmente: ponemos el valor opuesto al que intentamos
            setData((prev) => {
                if (!prev) return prev;
                const updated = prev.products.map((p) =>
                    selected.has(p.id) ? { ...p, is_featured: makeFeatured ? 0 : 1 } : p
                );
                return { ...prev, products: updated };
            });
            toast.error("Algunos elementos no pudieron actualizarse");
        } finally {
            setBusyFeatureIds((s) => {
                const n = new Set(s);
                ids.forEach((id) => n.delete(id));
                return n;
            });
            clearSelection();
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-gray-50">
                <div className="text-gray-500 animate-pulse">Cargando tienda…</div>
            </div>
        );
    }
    if (err || !data) {
        return (
            <div className="min-h-screen bg-gray-50 px-4 py-6">
                <div className="mx-auto max-w-6xl">
                    <Link to="/admin/users" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Volver
                    </Link>
                    <div className="rounded-2xl border bg-white p-6 text-red-600">{err ?? "No se encontró la tienda"}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Banner */}
            <div className="relative h-40 sm:h-56 bg-gray-200">
                {data.store_banner ? (
                    <img
                        src={fileUrl(UPLOADS_URL + data.store_banner)}
                        alt="Banner tienda"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full grid place-items-center text-gray-400">
                        <ImageIcon className="w-8 h-8" />
                    </div>
                )}

                {/* Header card */}
                <div className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2 w-[95%] sm:w-[85%]">
                    <div className="rounded-2xl border bg-white shadow-sm p-4 flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 border">
                            {data.store_logo ? (
                                <img src={fileUrl(UPLOADS_URL + data.store_logo)} alt={data.store_name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full grid place-items-center text-gray-400">
                                    <Store className="w-6 h-6" />
                                </div>
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-lg sm:text-xl font-bold text-gray-900">{data.store_name}</h1>
                                {data.verification_status === "verified" && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                        <ShieldCheck className="w-3.5 h-3.5" /> Verificada
                                    </span>
                                )}
                            </div>
                            {data.store_description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{data.store_description}</p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-3 text-sm">
                                <span className="inline-flex items-center gap-1 text-gray-700">
                                    <Star className="w-4 h-4 text-amber-500" /> {metrics.rating.toFixed(2)}
                                </span>
                                <span className="inline-flex items-center gap-1 text-gray-700">
                                    <ShoppingBag className="w-4 h-4" /> {metrics.sales} ventas
                                </span>
                                <span className="inline-flex items-center gap-1 text-gray-700">
                                    <Package className="w-4 h-4" /> {data.products?.length || 0} productos
                                </span>
                                <span className="inline-flex items-center gap-1 text-gray-500">
                                    <Users className="w-4 h-4" /> {data.total_reviews} reseñas
                                </span>
                            </div>
                        </div>
                        <div className="hidden sm:block">
                            <Button asChild variant="outline">
                                <Link to="/admin/users">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Volver
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="mx-auto max-w-6xl px-4 pt-24 pb-8 space-y-6">
                {/* Toolbar filtros y búsqueda */}
                <div className="rounded-2xl border bg-white p-3 sm:p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                        <div className="md:col-span-5">
                            <div className="relative">
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
                                    placeholder="Buscar por nombre o SKU…"
                                    value={q}
                                    onChange={(e) => { setQ(e.target.value); setPage(1); }}
                                />
                            </div>
                        </div>

                        <div className="md:col-span-3">
                            <div className="relative">
                                <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <select
                                    className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
                                    value={sortBy}
                                    onChange={(e) => {
                                        setSortBy(e.target.value as SortKey);
                                        setPage(1);
                                    }

                                    }
                                >
                                    {SORT_OPTIONS.map((o) => (
                                        <option value={o.value} key={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="md:col-span-2 flex items-center gap-3">
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={prioritizeFeatured} onChange={(e) => setPrioritizeFeatured(e.target.checked)} />
                                Priorizar destacados
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={filterFeatured} onChange={(e) => { setFilterFeatured(e.target.checked); setPage(1); }} />
                                Solo destacados
                            </label>
                        </div>

                        <div className="md:col-span-2 flex items-center gap-3 md:justify-end">
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={filterInStock} onChange={(e) => { setFilterInStock(e.target.checked); setPage(1); }} />
                                En stock
                            </label>
                            <select
                                className="border rounded-md px-2 py-2 text-sm"
                                value={filterStatus}
                                onChange={(e) => { setFilterStatus(e.target.value as any); setPage(1); }}
                            >
                                <option value="all">Todos</option>
                                <option value="active">Activos</option>
                                <option value="inactive">Inactivos</option>
                            </select>
                        </div>
                    </div>

                    {/* Toolbar de acciones masivas */}
                    {hasSelection && (
                        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3 mt-2">
                            <div className="text-sm text-gray-700">
                                {selected.size} seleccionados
                                {MAX_FEATURED > 0 && (
                                    <span className="ml-2 text-xs text-gray-500">
                                        Destacados: {currentFeaturedCount}/{MAX_FEATURED}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" onClick={() => bulkSetFeatured(true)}>
                                    Marcar como destacados
                                </Button>
                                <Button variant="outline" onClick={() => bulkSetFeatured(false)}>
                                    Quitar destacados
                                </Button>
                                <Button variant="outline" onClick={clearSelection}>
                                    Limpiar selección
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Cabecera de grid: selección página + perPage */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                        onClick={toggleSelectAllPage}
                        className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
                        title="Seleccionar todo en la página"
                    >
                        {pageItems.every((p) => selected.has(p.id)) && pageItems.length > 0 ? (
                            <CheckSquare className="w-4 h-4" />
                        ) : (
                            <Square className="w-4 h-4" />
                        )}
                        {pageItems.length > 0 ? "Seleccionar página" : "Sin resultados"}
                    </button>

                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">Por página</span>
                        <select
                            className="border rounded-md px-2 py-1.5 text-sm"
                            value={perPage}
                            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                        >
                            {[8, 12, 16, 24, 32].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                </div>

                {/* Grid de productos (paginado local) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {pageItems.map((p) => {
                        const price = toNum(p.price);
                        const compare = p.compare_price ? toNum(p.compare_price) : undefined;
                        const primary = p.images?.find((im) => im.is_primary) || p.images?.[0];
                        const img = primary?.image_url ? fileUrl(primary.image_url) : "";
                        const featured = isTrue(p.is_featured);
                        const busy = busyFeatureIds.has(p.id);
                        const checked = isSelected(p.id);

                        return (
                            <div key={p.id} className={`group rounded-xl border bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow ${checked ? "ring-2 ring-primary/30" : ""}`}>
                                <div className="relative">
                                    {/* Checkbox de selección */}
                                    <label className="absolute left-2 top-2 z-[2] bg-white/90 rounded-md px-1.5 py-1 cursor-pointer border shadow-sm">
                                        <input
                                            type="checkbox"
                                            className="align-middle"
                                            checked={checked}
                                            onChange={() => toggleSelect(p.id)}
                                        />
                                    </label>

                                    <Link to={`/product/${p.slug}`}>
                                        <div className="aspect-[4/3] bg-gray-100">
                                            {img ? (
                                                <img src={UPLOADS_URL + img} alt={p.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full grid place-items-center text-gray-400">
                                                    <ImageIcon className="w-6 h-6" />
                                                </div>
                                            )}
                                        </div>
                                    </Link>

                                    {/* Toggle destacado */}
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); toggleFeatured(p.id, p.is_featured); }}
                                        className={`absolute top-2 right-2 rounded-full border bg-white/95 backdrop-blur px-2.5 py-1.5 shadow-sm flex items-center gap-1 text-xs ${featured ? "text-amber-600 border-amber-200" : "text-gray-600 border-gray-200"
                                            } ${busy ? "opacity-60 pointer-events-none" : "hover:bg-white"}`}
                                        title={featured ? "Quitar de destacados" : "Marcar como destacado"}
                                    >
                                        {featured ? (
                                            <Star className="w-4 h-4 text-amber-500" fill="currentColor" />
                                        ) : (
                                            <StarOff className="w-4 h-4" />
                                        )}
                                        <span className="hidden sm:inline">{featured ? "Destacado" : "Destacar"}</span>
                                    </button>

                                    {/* Badges superpuestos */}
                                    <div className="absolute left-2 bottom-2 flex gap-1">
                                        {featured && <Badge variant="warning">Destacado</Badge>}
                                        {p.stock_quantity <= 0 && <Badge variant="destructive">Sin stock</Badge>}
                                        {p.status !== "active" && <Badge variant="outline">Inactivo</Badge>}
                                    </div>
                                </div>

                                <div className="p-3">
                                    <div className="text-sm font-medium text-gray-900 line-clamp-2">{p.name}</div>
                                    <div className="mt-1 flex items-baseline gap-2">
                                        <div className="text-base font-semibold text-gray-900">{formatPrice(price, "PEN")}</div>
                                        {typeof compare === "number" && compare > price && (
                                            <div className="text-xs text-gray-500 line-through">{formatPrice(compare, "PEN")}</div>
                                        )}
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">SKU: {p.sku}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Empty state */}
                {!loading && filteredSorted.length === 0 && (
                    <div className="rounded-2xl border bg-white p-8 text-center text-gray-600">
                        No se encontraron productos con los filtros actuales.
                    </div>
                )}

                {/* Paginación local */}
                {filteredSorted.length > 0 && (
                    <div className="mt-4 flex items-center justify-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            leftIcon={<ChevronLeft className="w-4 h-4" />}
                        >
                            Anterior
                        </Button>
                        <span className="text-sm text-gray-700">
                            Página <strong>{page}</strong> de <strong>{lastPage}</strong>
                            <span className="ml-2 text-gray-500">({filteredSorted.length} resultados)</span>
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                            disabled={page >= lastPage}
                            rightIcon={<ChevronRight className="w-4 h-4" />}
                        >
                            Siguiente
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSellerStorePage;
